document.addEventListener("DOMContentLoaded", function () {

  console.log("✅ VTO Script Loaded (new Zukti integration v1.3)");

  /* ============================================================
     🔑 ENVIRONMENT / CONFIG  — Zukti VTO Integration Guide v1.3
     ------------------------------------------------------------
     Everything environment-specific lives here. To go from
     STAGING → PRODUCTION you only change values in this block.

     Source-of-truth mapping (from the v1.3 guide):
       • 2D API (Part A)  base   → op-backend.zuktiinnovations.com
                          auth   → 909b5c1d493470dcc6d3f745156110ca   (Part A cURL "authorization")
                          x-domain → yourspex.zuktiinnovations.com     (Part A cURL "x-domain")
       • 3D/2D iframe (Part B) staging base → staging-iframe.zuktiinnovations.com
                          production base   → vto.zuktiinnovations.com
                          token       (URL param)         → 839b6cd94d714693884f667b12fdcc75
                          reference_key (URL param)        → 909b5c1d493470dcc6d3f745156110ca
                          client (URL param, HANDSHAKE_DOMAIN) → yourspex.zuktiinnovations.com

     ⚠️ CONFIRM WITH ZUKTI BEFORE PROD (these caused the "old vs new"
        confusion in the email thread):
        1. x-domain / client value — the guide flags it explicitly.
           If the storefront has both a *.myshopify.com and a custom
           domain, confirm which one the token is provisioned against.
        2. Whether 909b… is the STAGING or PROD 2D-API token (Part A
           gives only one host but the shown token looks staging-flavoured).
        3. The shape of the SSE frame "image" field — see
           resolveFrameImageSrc() below. Verify on staging first.
  ============================================================ */

  /* ---- 2D VTO API (Part A — encrypted) ---- */
  const VTO_2D_API_BASE   = "https://api.optics.zuktiinnovations.com";
  const VTO_2D_AUTH_TOKEN = "1b6ecf103f38deb471dee21ce786b6ae";
  const VTO_2D_X_DOMAIN   = "yourspex.vto-backend.zuktiinnovations.com";

  /* ---- 3D / 2D-capture iframe (Part B) ---- */
  const VTO_IFRAME_BASE   = "https://facefit-360.zuktiinnovations.com/";
  const VTO_IFRAME_TOKEN  = "50d259d7f4944eff92444bc20a08eeec";
  const VTO_REFERENCE_KEY = "1b6ecf103f38deb471dee21ce786b6ae";
  const VTO_CLIENT_DOMAIN = "yourspex.vto-backend.zuktiinnovations.com"; // ⚠️ see note

  const BATCH_SIZE = 10; // Send ~10 SKUs per try-on request

  /* HKDF "info" string — MUST match the backend exactly (guide Step 3). */
  const VTO_HKDF_INFO = "selfie-upload-session";

  /* =========================
     🔑 UUID GENERATOR
  ========================= */
  function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /* =========================
     👤 USER ID HANDLER
     (UNCHANGED — the same user_id links the iframe selfie upload to
      the 2D API render, so it must stay identical.)
  ========================= */
  function getOrCreateUserId() {
    const key = "vto_user_id";

    /* ✅ LOGGED-IN USER (FROM LIQUID) */
    if (window.shopifyCustomerId) {
      const customerId = "user_" + window.shopifyCustomerId;
      localStorage.setItem(key, customerId);
      console.log("👤 Logged-in User ID:", customerId);
      return customerId;
    }

    /* ✅ GUEST USER */
    let stored = localStorage.getItem(key);

    if (!stored || !stored.startsWith("guest_")) {
      const guestId = "guest_" + generateUUID();
      localStorage.setItem(key, guestId);
      console.log("👤 New Guest User ID:", guestId);
      return guestId;
    }

    console.log("👤 Existing Guest ID:", stored);
    return stored;
  }

  /* Initialize user ID */
  getOrCreateUserId();

  /* =========================
     📦 STATE MANAGEMENT
  ========================= */
  const vtoState = {
    imageUploaded: false,       // ⚠️ NOT persisted — cleared on every page load/refresh
    userId: null,               // Lives in localStorage via getOrCreateUserId()
    pdResult: null,             // Lives in sessionStorage (clears on tab close)
    processedSkus: [],          // ⚠️ NOT persisted — SKUs already submitted this session
    renderedImages: {},         // ⚠️ Memory-only cache: { skuId: imageSrc } from SSE frames
    isProcessing: false,        // Runtime flag only
    toggleOn: false             // ⚠️ Disabled/OFF by default on page load
  };

  /* =========================
     🔐 2D VTO CRYPTO SESSION  (Part A, Steps 1–5)
     Memory-only per the guide — NEVER persist keys to storage.
  ========================= */
  const vtoCrypto = {
    keyPair: null,
    aesKey: null,
    requestRef: null
  };

  function vto2dHeaders() {
    return {
      "Content-Type": "application/json",
      "Authorization": VTO_2D_AUTH_TOKEN, // raw token, no "Bearer"
      "x-domain": VTO_2D_X_DOMAIN
    };
  }

  /* Step 3 — derive the shared AES-256-GCM key from the server public key */
  async function deriveAesKey(keyPair, serverPublicKeyB64) {
    const serverPubBytes = Uint8Array.from(atob(serverPublicKeyB64), c => c.charCodeAt(0));
    const serverPublicKey = await window.crypto.subtle.importKey(
      "raw", serverPubBytes, { name: "ECDH", namedCurve: "P-256" }, true, []
    );
    const sharedBits = await window.crypto.subtle.deriveBits(
      { name: "ECDH", public: serverPublicKey }, keyPair.privateKey, 256
    );
    const hkdfMaterial = await window.crypto.subtle.importKey(
      "raw", sharedBits, "HKDF", false, ["deriveKey"]
    );
    return window.crypto.subtle.deriveKey(
      { name: "HKDF", hash: "SHA-256", salt: new Uint8Array(0),
        info: new TextEncoder().encode(VTO_HKDF_INFO) },
      hkdfMaterial, { name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]
    );
  }

  /* Steps 1–3 — full handshake, caches aesKey + requestRef in module memory */
  async function performHandshake() {
    try {
      // Step 1 — ECDH key pair
      const keyPair = await window.crypto.subtle.generateKey(
        { name: "ECDH", namedCurve: "P-256" }, true, ["deriveBits"]
      );
      const rawPublicKey = await window.crypto.subtle.exportKey("raw", keyPair.publicKey);
      const publicKeyB64 = btoa(String.fromCharCode(...new Uint8Array(rawPublicKey)));

      // Step 2 — handshake exchange
      const resp = await fetch(VTO_2D_API_BASE + "/handshake/", {
        method: "POST",
        headers: vto2dHeaders(),
        body: JSON.stringify({ client_public_key: publicKeyB64 })
      });

      if (!resp.ok) {
        console.error("❌ [VTO] Handshake failed:", resp.status, resp.statusText);
        return false;
      }

      const { request_ref, server_public_key } = await resp.json();
      if (!request_ref || !server_public_key) {
        console.error("❌ [VTO] Handshake response missing request_ref / server_public_key");
        return false;
      }

      // Step 3 — derive shared AES key
      const aesKey = await deriveAesKey(keyPair, server_public_key);

      vtoCrypto.keyPair = keyPair;
      vtoCrypto.aesKey = aesKey;
      vtoCrypto.requestRef = request_ref;

      console.log("🤝 [VTO] Handshake complete, session established.");
      return true;
    } catch (err) {
      console.error("❌ [VTO] Handshake error:", err);
      return false;
    }
  }

  /* Re-uses the cached session; only re-handshakes when forced or empty */
  async function ensureSession(forceNew) {
    if (!forceNew && vtoCrypto.aesKey && vtoCrypto.requestRef) return true;
    return performHandshake();
  }

  /* Step 4 — encryption helper (returns the base64 "packet") */
  async function encryptPayload(data, aesKey) {
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const plaintext = new TextEncoder().encode(JSON.stringify(data));
    const cipherBuf = await window.crypto.subtle.encrypt({ name: "AES-GCM", iv }, aesKey, plaintext);
    const cipherBytes = new Uint8Array(cipherBuf);
    const tag = cipherBytes.slice(-16);
    const ciphertext = cipherBytes.slice(0, -16);
    const b64 = (buf) => btoa(String.fromCharCode(...buf));
    return btoa(JSON.stringify({ iv: b64(iv), ciphertext: b64(ciphertext), tag: b64(tag) }));
  }

  /* Step 5 — decryption helper (takes a base64 packet, returns parsed frame) */
  async function decryptPayload(packetB64, aesKey) {
    const envelope = JSON.parse(atob(packetB64));
    const fromB64 = (s) => Uint8Array.from(atob(s), c => c.charCodeAt(0));
    const iv = fromB64(envelope.iv);
    const ciphertext = fromB64(envelope.ciphertext);
    const tag = fromB64(envelope.tag);
    const combined = new Uint8Array(ciphertext.length + tag.length);
    combined.set(ciphertext, 0);
    combined.set(tag, ciphertext.length);
    const plainBuf = await window.crypto.subtle.decrypt({ name: "AES-GCM", iv }, aesKey, combined);
    return JSON.parse(new TextDecoder().decode(plainBuf));
  }

  /* =========================
     🗄️ SESSION STORAGE HELPERS
     (UNCHANGED — only pdResult + toggle preference. Keys are never
      persisted; renderedImages stays in memory only.)
  ========================= */
  function savePDToSession(pdData) {
    try {
      sessionStorage.setItem("vto_pd_result", JSON.stringify(pdData));
    } catch (e) {
      console.warn("⚠️ Could not save PD to sessionStorage:", e);
    }
  }

  function getPDFromSession() {
    try {
      const saved = sessionStorage.getItem("vto_pd_result");
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  }

  function restoreVTOState() {
    try {
      const savedPD = getPDFromSession();
      if (savedPD) {
        vtoState.pdResult = savedPD;
        console.log("📦 VTO PD restored from sessionStorage.");
      }

      vtoState.toggleOn = false;
      localStorage.removeItem("vto_2d_state");

      console.log("📦 VTO State Initialised:", {
        hasPD: !!vtoState.pdResult,
        toggleOn: vtoState.toggleOn,
        imageUploaded: false
      });
    } catch (e) {
      console.warn("⚠️ Could not restore VTO state:", e);
    }
  }

  function saveVTOState() {
    try {
      if (vtoState.pdResult) {
        savePDToSession(vtoState.pdResult);
      }
      sessionStorage.setItem("vto_toggle_on", JSON.stringify(vtoState.toggleOn));
    } catch (e) {
      console.warn("⚠️ Could not save VTO state:", e);
    }
  }

  restoreVTOState();

  /* =========================
     🚀 OPEN VTO IFRAME (2D + 3D)
     CHANGED: new base URL + adds client & reference_key params.
     Everything else (modal wiring, parentUrl postMessage) is UNCHANGED.
  ========================= */
  window.VTO_STATE = window.VTO_STATE || {
    activeSku: null,
    activeVariantId: null,
    activeVariantPrice: null
  };

  function openVTO(skuId, type) {
    console.log(`🚀 openVTO called for ${type}`);

    const userId = getOrCreateUserId();
    const targetSku = (window.VTO_STATE && window.VTO_STATE.activeSku) ? window.VTO_STATE.activeSku : skuId;

    if (!userId || !targetSku || !VTO_IFRAME_TOKEN) {
      console.error("❌ Missing Data", { userId, skuId: targetSku, token: VTO_IFRAME_TOKEN });
      return;
    }

    // Store current userId for 2D flow
    vtoState.userId = userId;
    saveVTOState();

    const is2D = type === "2D" ? "true" : "false";

    // NEW parameter set (Part B). Order does not matter; Base64-encoded as before.
    const params = new URLSearchParams({
      token: VTO_IFRAME_TOKEN,
      user_id: userId,
      sku_id: targetSku,
      client: VTO_CLIENT_DOMAIN,          // HANDSHAKE_DOMAIN
      reference_key: VTO_REFERENCE_KEY,   // HANDSHAKE_TOKEN
      is2D_VTO: is2D
    });
    const encoded = btoa(params.toString());
    const iframeUrl = `${VTO_IFRAME_BASE}?${encoded}`;

    console.log("👤 User:", userId);
    console.log("🛍 SKU:", targetSku);
    console.log("🎯 Mode:", is2D);
    console.log("🌐 Iframe URL:", iframeUrl);

    const iframe = document.getElementById("vto-iframe");
    const modal = document.getElementById("vto-modal");
    const buyActions = modal ? modal.querySelector(".vto-buy-actions") : null;

    if (iframe && modal) {
      modal.setAttribute("data-vto-type", type);
      if (buyActions) {
        buyActions.style.display = (type === "2D") ? "none" : "flex";
      }
      modal.style.display = "flex";
      document.body.style.overflow = "hidden";
      iframe.src = iframeUrl;

      iframe.onload = () => {
        // Part B Step 3A — parent MUST post its URL for domain whitelist verification.
        const parentUrl = window.location.href.split("?")[0];
        console.log("📡 Iframe loaded, sending parentUrl:", parentUrl);
        iframe.contentWindow.postMessage({ parentUrl: parentUrl }, "*");
      };
    } else {
      window.open(iframeUrl, "_blank");
    }
  }

  /* =========================
     📨 CLOSE VTO IFRAME
     CHANGED: now posts { action: "close", type: "VTO_CLOSE" } to the
     iframe's contentWindow BEFORE the src is cleared (new backend
     requirement from Sharan's email — the iframe needs this message
     to clean up its own session on close). Everything else UNCHANGED.
  ========================= */
  function closeVTOModal() {
    const modal = document.getElementById("vto-modal");
    const iframe = document.getElementById("vto-iframe");
    const wasType = modal ? modal.getAttribute("data-vto-type") : null;

    // 🔔 NEW — notify the iframe it's being closed, per backend request.
    // Must fire before iframe.src is cleared below, or contentWindow is gone.
    if (iframe && iframe.contentWindow) {
      try {
        iframe.contentWindow.postMessage(
          { action: "close", type: "VTO_CLOSE" },
          "*"
        );
      } catch (e) {
        console.warn("⚠️ [VTO] Could not post VTO_CLOSE to iframe:", e);
      }
    }

    if (modal && iframe) {
      modal.style.display = "none";
      iframe.src = "";
      document.body.style.overflow = "";
      console.log("✅ VTO Modal Closed");
    }

    // If the user switched frames inside the 3D modal, repaint the PDP now so
    // the visible product (image, title, price, swatch selection) matches the
    // variant they picked — using the theme's own swatch handler. The buy/cart
    // wiring was already synced live during the switch; this just repaints UI.
    if (wasType === "3D" && vtoPendingVariantSku) {
      const sku = vtoPendingVariantSku;
      vtoPendingVariantSku = null;
      // Defer to next frame so the modal is fully hidden before #MainContent swaps.
      requestAnimationFrame(() => syncPdpUiToVariant(sku));
    } else {
      vtoPendingVariantSku = null;
    }

    if (!vtoState.imageUploaded) {
      const toggle = document.getElementById("vto-2d-toggle");
      if (toggle && toggle.checked) {
        toggle.checked = false;
      }
      vtoState.toggleOn = false;
      saveVTOState();
      applyToggleState();
    }
  }

  /* =========================
     📊 SHOW PD RESULTS POPUP  (UNCHANGED)
     Note: the 2D flow bypasses PD calculation, so this stays as a
     harmless fallback for any PD messages the iframe may still post.
  ========================= */
  function showPDResults(pdData) {
    const popup = document.getElementById("vto-pd-results-popup");
    const body = document.getElementById("vto-pd-results-body");

    if (!popup || !body) return;

    console.log("📊 showPDResults called with:", JSON.stringify(pdData, null, 2));

    let html = "";

    if (pdData && typeof pdData === "object") {
      const leftPD = pdData.left_eye_pd || pdData.left_pd || pdData.leftPD || 0;
      const rightPD = pdData.right_eye_pd || pdData.right_pd || pdData.rightPD || 0;
      const totalPD = pdData.pd || pdData.PD || (leftPD + rightPD) || 0;
      const leftEyeWidth = pdData.lefteyewidth_mm || 0;
      const rightEyeWidth = pdData.righteyewidth_mm || 0;
      const noseBridge = pdData.nosebridge_mm || 0;

      console.log("📊 Extracted PD values:", { totalPD, leftPD, rightPD, leftEyeWidth, rightEyeWidth, noseBridge });

      if (totalPD) {
        html += `<div class="pd-value">
          <span class="pd-number">${totalPD}</span>
          <span class="pd-unit">mm</span>
        </div>
        <p style="text-align:center; font-size:1.3rem; color:#888; margin-top:0.4rem;">Total PD</p>`;
      }

      if (leftPD || rightPD) {
        html += `<div style="display:flex; justify-content:space-around; margin-top:2rem; padding:1.6rem; background:#f9f9f9; border-radius:1rem;">`;
        if (leftPD) {
          html += `<div style="text-align:center;">
            <p style="font-size:1.3rem; color:#888; margin-bottom:0.4rem;">Left Eye PD</p>
            <p style="font-size:2.4rem; font-weight:700; color:#252E40; margin:0;">${leftPD} <span style="font-size:1.4rem; color:#888;">mm</span></p>
          </div>`;
        }
        if (rightPD) {
          html += `<div style="text-align:center;">
            <p style="font-size:1.3rem; color:#888; margin-bottom:0.4rem;">Right Eye PD</p>
            <p style="font-size:2.4rem; font-weight:700; color:#252E40; margin:0;">${rightPD} <span style="font-size:1.4rem; color:#888;">mm</span></p>
          </div>`;
        }
        html += `</div>`;
      }

      if (leftEyeWidth || rightEyeWidth || noseBridge) {
        html += `<div style="display:flex; justify-content:space-around; margin-top:1.2rem; padding:1.2rem; background:#f9f9f9; border-radius:1rem; flex-wrap:wrap; gap:0.8rem;">`;
        if (leftEyeWidth) {
          html += `<div style="text-align:center; flex:1; min-width:80px;">
            <p style="font-size:1.2rem; color:#888; margin-bottom:0.2rem;">Left Eye Width</p>
            <p style="font-size:1.6rem; font-weight:600; color:#252E40; margin:0;">${leftEyeWidth} mm</p>
          </div>`;
        }
        if (noseBridge) {
          html += `<div style="text-align:center; flex:1; min-width:80px;">
            <p style="font-size:1.2rem; color:#888; margin-bottom:0.2rem;">Nose Bridge</p>
            <p style="font-size:1.6rem; font-weight:600; color:#252E40; margin:0;">${noseBridge} mm</p>
          </div>`;
        }
        if (rightEyeWidth) {
          html += `<div style="text-align:center; flex:1; min-width:80px;">
            <p style="font-size:1.2rem; color:#888; margin-bottom:0.2rem;">Right Eye Width</p>
            <p style="font-size:1.6rem; font-weight:600; color:#252E40; margin:0;">${rightEyeWidth} mm</p>
          </div>`;
        }
        html += `</div>`;
      }

      if (!totalPD && !leftPD && !rightPD) {
        html += `<p style="font-weight:600; color:#252E40;">PD measurement completed.</p>`;
        html += `<pre style="font-size:1.2rem; background:#f4f4f4; padding:1rem; border-radius:0.8rem; overflow-x:auto;">${JSON.stringify(pdData, null, 2)}</pre>`;
      }

    } else if (typeof pdData === "string" || typeof pdData === "number") {
      html += `<div class="pd-value">
        <span class="pd-number">${pdData}</span>
        <span class="pd-unit">mm</span>
      </div>`;
    } else {
      html += `<p>PD measurement completed successfully.</p>`;
    }

    body.innerHTML = html;
    popup.style.display = "flex";
    document.body.style.overflow = "hidden";
  }

  function closePDPopup() {
    const popup = document.getElementById("vto-pd-results-popup");
    if (popup) {
      popup.style.display = "none";
      document.body.style.overflow = "";
    }
  }

  /* =========================
     🖼️ 2D VTO — SKU COLLECTION  (UNCHANGED)
  ========================= */
  function collectVTOSkus() {
    const cards = document.querySelectorAll('marmeto-product-card[data-vto-2d="true"]');
    const skus = [];

    cards.forEach(card => {
      const sku = card.getAttribute("data-product-sku");
      if (sku && sku.trim()) {
        skus.push({ skuid: sku.trim() });
      }
    });

    console.log(`📋 Collected ${skus.length} VTO SKUs from page:`, skus.map(s => s.skuid));
    return skus;
  }

  function batchSkus(skus) {
    const batches = [];
    for (let i = 0; i < skus.length; i += BATCH_SIZE) {
      batches.push(skus.slice(i, i + BATCH_SIZE));
    }
    console.log(`📦 Split into ${batches.length} batches of ~${BATCH_SIZE}`);
    return batches;
  }

  /* =========================
     🖼️ 2D VTO — NEW ENCRYPTED TRY-ON (Part A, Steps 6–7)
  ========================= */

  /**
   * Resolve the image source out of an SSE frame.
   * ⚠️ VERIFY ON STAGING: the guide's renderTryOnFrame() comment shows a
   * frame shaped { sku_id, image, success, progress, total, done } but does
   * not pin down whether `image` is a full URL, a data: URI, or bare base64.
   * This handles all three. If the backend instead returns an S3 *path*,
   * tell me the pattern and I'll add it here.
   */
  function resolveFrameImageSrc(frame) {
    const raw = frame.image || frame.image_url || frame.url || frame.data;
    if (!raw || typeof raw !== "string") return null;
    if (/^data:/i.test(raw) || /^https?:\/\//i.test(raw)) return raw;
    // Bare base64 → assume JPEG
    return "data:image/jpeg;base64," + raw;
  }

  /**
   * Submit one batch of SKUs to POST /vto-images-data/ and consume the
   * encrypted SSE stream, rendering each frame as it arrives.
   * Returns { submitted:[skuId], selfieExpired?:bool, sessionError?:bool }.
   */
  async function submitTryOnBatch(batch, userId, isRetry) {
    const ok = await ensureSession();
    if (!ok) return { submitted: [], sessionError: true };

    const payload = { user_id: userId, sku_ids: batch.map(s => ({ skuid: s.skuid })) };

    let packet;
    try {
      packet = await encryptPayload(payload, vtoCrypto.aesKey);
    } catch (e) {
      console.error("❌ [VTO] Encrypt failed:", e);
      return { submitted: [], sessionError: true };
    }

    let resp;
    try {
      resp = await fetch(VTO_2D_API_BASE + "/vto-images-data/", {
        method: "POST",
        headers: vto2dHeaders(),
        body: JSON.stringify({ request_ref: vtoCrypto.requestRef, packet })
      });
    } catch (err) {
      console.error("❌ [VTO] Try-on request network error:", err);
      return { submitted: [], sessionError: true };
    }

    // 401 → session/token expired. Re-handshake once (guide §5.1) then retry.
    if (resp.status === 401) {
      console.warn("🔄 [VTO] 401 on try-on — re-handshaking once...");
      if (!isRetry) {
        const re = await ensureSession(true);
        if (re) return submitTryOnBatch(batch, userId, true);
      }
      return { submitted: [], sessionError: true };
    }

    // Non-OK (non-stream) response → could be an expired-selfie error.
    if (!resp.ok) {
      let bodyText = "";
      try { bodyText = await resp.text(); } catch (e) {}
      console.warn("⚠️ [VTO] Try-on non-OK:", resp.status, bodyText);
      if (/selfie|expired|not[-_ ]?found/i.test(bodyText)) {
        return { submitted: [], selfieExpired: true };
      }
      return { submitted: [], sessionError: true };
    }

    // Step 7 — process streamed SSE results.
    const seen = await readTryOnStream(resp, batch);

    // Any SKU in the batch that never produced a frame → per-SKU fallback.
    batch.forEach(s => {
      if (!seen.has(s.skuid)) applyImageToCards(s.skuid, null, false);
    });

    return { submitted: batch.map(s => s.skuid) };
  }

  /**
   * Read and decrypt the SSE stream. Renders each frame; returns the set of
   * sku_ids that were seen so the caller can fall back on the rest.
   */
  async function readTryOnStream(resp, batch) {
    const seen = new Set();
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const events = buffer.split("\n\n");
      buffer = events.pop();

      for (const evt of events) {
        // Join any/all "data:" lines in this event block.
        const dataStr = evt
          .split("\n")
          .filter(l => l.trimStart().startsWith("data:"))
          .map(l => l.replace(/^\s*data:\s?/, ""))
          .join("");
        if (!dataStr) continue;

        let outer;
        try { outer = JSON.parse(dataStr); } catch (e) { continue; }
        const enc = outer.payload;
        if (!enc) continue;

        let frame;
        try {
          frame = await decryptPayload(enc, vtoCrypto.aesKey);
        } catch (e) {
          console.warn("⚠️ [VTO] Frame decrypt failed (session may have rotated):", e);
          continue;
        }

        if (frame && frame.sku_id) {
          seen.add(frame.sku_id);
          renderTryOnFrame(frame);
        }
      }
    }
    return seen;
  }

  /**
   * Render a single decrypted try-on frame onto the matching product card(s).
   * Caches the resolved image src so injected/re-scrolled cards can re-apply
   * without another network call.
   */
  function renderTryOnFrame(frame) {
    const src = resolveFrameImageSrc(frame);
    if (frame.success !== false && src) {
      vtoState.renderedImages[frame.sku_id] = src;
      applyImageToCards(frame.sku_id, src, true);
    } else {
      applyImageToCards(frame.sku_id, null, false);
    }
  }

  /**
   * Apply an image src (or the "not available" fallback) to every overlay
   * for a SKU. Mirrors the old per-card image swap and fallback markup.
   */
  function applyImageToCards(skuId, src, success) {
    const overlays = document.querySelectorAll(`.product-card-vto-overlay[data-vto-sku="${skuId}"]`);

    if (overlays.length === 0) {
      console.log(`🖼️ [SKU: ${skuId}] No overlay containers found (card may not be in DOM yet).`);
      return;
    }

    overlays.forEach(overlay => {
      if (!success || !src) {
        overlay.classList.remove("vto-loading");
        overlay.innerHTML = '<div class="tw-flex tw-items-center tw-justify-center tw-w-full tw-h-full tw-bg-[#f5f5f5] tw-text-center tw-p-[1rem]"><p class="tw-text-[1.2rem] tw-text-[#252E40] tw-font-medium">2D Try-On not available for this frame</p></div>';
        overlay.style.display = "block";
        return;
      }

      // Re-create the img element to clear any stale src/onerror state.
      const oldImg = overlay.querySelector(".vto-user-image");
      const freshImg = document.createElement("img");
      freshImg.className = "vto-user-image";
      freshImg.setAttribute("data-original-alt", (oldImg && oldImg.alt) || `2D Try-On preview for ${skuId}`);
      freshImg.alt = "";
      freshImg.style.opacity = "0";
      freshImg.style.transition = "opacity 0.3s ease-in";
      freshImg.setAttribute("loading", "lazy");

      // Force the image to fill the card. The new SSE-delivered image is a
      // base64 data URI with a different (shorter) aspect ratio than the old
      // S3 image, so relying on the stylesheet's height leaves a gap. The theme
      // rule `#product-grid .product-card img { height:auto; max-height:300px }`
      // uses an ID selector and out-specificities `.vto-user-image`, so we set
      // these inline with !important to guarantee a full-height, cropped fill.
      freshImg.style.setProperty("width", "100%", "important");
      freshImg.style.setProperty("height", "100%", "important");
      freshImg.style.setProperty("max-height", "none", "important");
      freshImg.style.setProperty("object-fit", "cover", "important");
      freshImg.style.setProperty("object-position", "center", "important");

      freshImg.onload = function () {
        overlay.classList.remove("vto-loading");
        freshImg.style.opacity = "1";
        const a = freshImg.getAttribute("data-original-alt");
        if (a) freshImg.alt = a;
        console.log(`✅ VTO image applied for SKU: ${skuId}`);
      };

      freshImg.onerror = function () {
        overlay.classList.remove("vto-loading");
        overlay.innerHTML = '<div class="tw-flex tw-items-center tw-justify-center tw-w-full tw-h-full tw-bg-[#f5f5f5] tw-text-center tw-p-[1rem]"><p class="tw-text-[1.2rem] tw-text-[#252E40] tw-font-medium">2D Try-On not available for this frame</p></div>';
        overlay.style.display = "block";
        console.error(`❌ [SKU: ${skuId}] Image failed to render.`);
      };

      // Ensure the overlay actually contains our fresh img.
      if (oldImg) {
        oldImg.replaceWith(freshImg);
      } else {
        overlay.innerHTML = "";
        overlay.appendChild(freshImg);
      }

      overlay.classList.add("vto-loading");
      overlay.style.display = "block";
      freshImg.src = src;
    });
  }

  /**
   * Re-apply already-rendered images to (re)injected cards — used by the
   * load-previous and infinite-scroll observers. Pulls from the memory cache;
   * SKUs not yet rendered are handled by the scroll handler's submit path.
   */
  function reapplyRenderedImages(skuIds) {
    (skuIds || []).forEach(skuId => {
      const src = vtoState.renderedImages[skuId];
      if (src) applyImageToCards(skuId, src, true);
    });
  }

  // Back-compat shim: observers call this to re-apply existing renders.
  function updateProductCardImages(skuIds) {
    reapplyRenderedImages(skuIds);
  }

  /* Re-open the capture modal when the backend reports an expired selfie. */
  function promptRecapture() {
    const allSkus = collectVTOSkus();
    const firstSku = (allSkus.length > 0) ? allSkus[0].skuid : null;
    if (firstSku) {
      console.log("📸 [VTO] Selfie expired — prompting recapture.");
      openVTO(firstSku, "2D");
    }
  }

  /**
   * Process ALL VTO SKUs on the page (called after a fresh capture).
   * Handshake once, then submit in batches (each batch reuses the session).
   */
  async function processAllVTOSkus(isRecapture = false) {
    if (vtoState.isProcessing) {
      console.log("⏳ Already processing VTO SKUs...");
      return;
    }

    if (isRecapture) {
      console.log("🔄 Recapture — clearing processed SKUs & rendered cache...");
      vtoState.processedSkus = [];
      vtoState.renderedImages = {};
    }

    const userId = vtoState.userId;
    if (!userId) {
      console.error("❌ No user ID available for 2D VTO");
      return;
    }

    const allSkus = collectVTOSkus();
    if (allSkus.length === 0) {
      console.log("ℹ️ No VTO-2D products found on page");
      return;
    }

    vtoState.isProcessing = true;

    // Establish the encrypted session once up front.
    const sessionOk = await ensureSession();
    if (!sessionOk) {
      console.error("❌ [VTO] Could not establish 2D session — aborting render.");
      vtoState.isProcessing = false;
      // Leave toggle OFF so the user isn't stuck "on" with no images.
      const toggle = document.getElementById("vto-2d-toggle");
      if (toggle) toggle.checked = false;
      vtoState.toggleOn = false;
      saveVTOState();
      applyToggleState();
      return;
    }

    const batches = batchSkus(allSkus);
    console.log(`🚀 Processing ${allSkus.length} SKUs in ${batches.length} batches for user ${userId}`);

    for (let i = 0; i < batches.length; i++) {
      console.log(`⏳ Batch ${i + 1}/${batches.length}...`, batches[i].map(s => s.skuid));
      const res = await submitTryOnBatch(batches[i], userId);

      if (res.selfieExpired) {
        vtoState.isProcessing = false;
        promptRecapture();
        return;
      }

      (res.submitted || []).forEach(id => {
        if (!vtoState.processedSkus.includes(id)) vtoState.processedSkus.push(id);
      });
    }

    vtoState.imageUploaded = true;
    vtoState.toggleOn = true;
    const toggle = document.getElementById("vto-2d-toggle");
    if (toggle) toggle.checked = true;
    saveVTOState();

    vtoState.isProcessing = false;
    applyToggleState();

    console.log(`✅ 2D VTO processing complete. ${vtoState.processedSkus.length} SKUs submitted.`);
  }

  /* =========================
     🔄 TOGGLE HANDLER  (UNCHANGED)
  ========================= */
  function applyToggleState() {
    const cards = document.querySelectorAll('marmeto-product-card[data-vto-2d="true"]');

    cards.forEach(card => {
      const imgContainer = card.querySelector(".vto-image-container");
      const overlay = card.querySelector(".product-card-vto-overlay");

      if (!imgContainer || !overlay) return;

      if (vtoState.toggleOn && vtoState.imageUploaded) {
        imgContainer.classList.add("vto-active");
        imgContainer.classList.remove("vto-default");
      } else {
        imgContainer.classList.remove("vto-active");
        imgContainer.classList.add("vto-default");
      }
    });

    const retakeBtn = document.getElementById("vto-retake-btn");
    if (retakeBtn) {
      retakeBtn.style.display = (vtoState.toggleOn && vtoState.imageUploaded) ? "inline-flex" : "none";
    }
  }

  function initToggle() {
    const toggle = document.getElementById("vto-2d-toggle");
    if (!toggle) return;

    toggle.checked = vtoState.toggleOn && vtoState.imageUploaded;
    applyToggleState();

    toggle.addEventListener("change", function () {
      if (this.checked) {
        if (!vtoState.imageUploaded) {
          const allSkus = collectVTOSkus();
          const firstSku = (allSkus.length > 0) ? allSkus[0].skuid : null;

          if (firstSku) {
            openVTO(firstSku, "2D");
          } else {
            const anyCard = document.querySelector('marmeto-product-card[data-product-sku]');
            const fallbackSku = anyCard ? anyCard.getAttribute('data-product-sku') : null;
            if (fallbackSku) {
              openVTO(fallbackSku, "2D");
            } else {
              console.warn("⚠️ [VTO] No product SKU found to launch 2D VTO modal.");
            }
          }
        } else {
          vtoState.toggleOn = true;
          saveVTOState();
          applyToggleState();
        }
      } else {
        vtoState.toggleOn = false;
        saveVTOState();
        applyToggleState();
      }
    });
  }

  initToggle();

  /* =========================
     👓 UPDATE ACTIVE PRODUCT VARIANT (3D VTO FRAME SWITCH)
     Parent-side logic driven by the iframe's frame-switch postMessage.
  ========================= */
  let initialDrawerHtml = null;
  let initialProductPath = null;

  // Tracks the variant the user selected inside the 3D modal so we can repaint
  // the PDP UI once, on modal close, using the theme's OWN swatch mechanism.
  // (These are "product as variant" links, not native Shopify variants — the
  // real UI repaint is CustomProductAsVariant.onVariantClick(), which fetches
  // the target product page and swaps #MainContent.innerHTML.)
  let vtoPendingVariantSku = null;

  /**
   * Find the matching <custom-product-as-variant> swatch element for a SKU and
   * trigger its real click handler, so the PDP repaints exactly as it would if
   * the user had clicked that swatch on the page. Safe to run while the VTO
   * modal is open, because the modal (#vto-modal) lives OUTSIDE #MainContent
   * and is therefore not destroyed when #MainContent.innerHTML is replaced.
   * Returns true if a swatch was triggered.
   */
  function syncPdpUiToVariant(skuId) {
    if (!skuId) return false;
    const target = String(skuId).trim().toLowerCase();

    const swatches = document.querySelectorAll("custom-product-as-variant[data-variant-sku]");
    let matchEl = null;

    // Exact SKU match first, then prefix/base match (mirrors findVariantBySku).
    swatches.forEach((el) => {
      if (matchEl) return;
      const sku = String(el.getAttribute("data-variant-sku") || "").trim().toLowerCase();
      if (sku === target) matchEl = el;
    });
    if (!matchEl) {
      swatches.forEach((el) => {
        if (matchEl) return;
        const sku = String(el.getAttribute("data-variant-sku") || "").trim().toLowerCase();
        if (sku && (sku.startsWith(target + "-c") || sku.startsWith(target))) matchEl = el;
      });
    }

    if (!matchEl) {
      console.log(`👓 [VTO] No custom-product-as-variant swatch found for SKU ${skuId} — skipping PDP repaint.`);
      return false;
    }

    // If this swatch is already the current page, nothing to repaint.
    const swatchUrl = matchEl.getAttribute("data-variant-url") || "";
    const swatchPath = swatchUrl ? new URL(swatchUrl, window.location.origin).pathname : null;
    if (swatchPath && swatchPath === window.location.pathname.split("?")[0]) {
      console.log("👓 [VTO] Selected variant is already the current page — no repaint needed.");
      return false;
    }

    console.log("👓 [VTO] Repainting PDP via custom-product-as-variant click for SKU:", skuId);
    // Trigger the theme's own handler (fetch target page → swap #MainContent + updateUrl()).
    matchEl.click();
    return true;
  }

  async function updateDrawerFlowForVariant(targetVariant) {
    if (!targetVariant) return;

    const currentDrawer = document.querySelector("drawer-flow");
    if (!currentDrawer) return;

    if (currentDrawer.classList.contains("active")) {
      return;
    }

    if (!initialDrawerHtml) {
      initialDrawerHtml = currentDrawer.innerHTML;
      initialProductPath = window.location.pathname.split('?')[0];
    }

    const targetUrl = targetVariant.url || initialProductPath;
    const targetPath = new URL(targetUrl, window.location.origin).pathname;

    if (targetPath === initialProductPath) {
      if (!currentDrawer.classList.contains("active")) {
        currentDrawer.innerHTML = initialDrawerHtml;
        if (typeof currentDrawer.initializeVariables === "function") {
          currentDrawer._initialized = false;
          currentDrawer.initializeVariables();
        }
      }
      if (window.location.pathname !== targetPath) {
        window.history.replaceState({}, "", targetUrl);
      }
      return;
    }

    try {
      const response = await fetch(targetUrl);
      if (!response.ok) {
        console.warn(`⚠️ [VTO] Failed to fetch product page HTML for ${targetUrl}: status ${response.status}`);
        return;
      }
      const htmlText = await response.text();
      const doc = new DOMParser().parseFromString(htmlText, "text/html");

      const newDrawer = doc.querySelector("drawer-flow");

      if (newDrawer && !currentDrawer.classList.contains("active")) {
        currentDrawer.innerHTML = newDrawer.innerHTML;
        if (typeof currentDrawer.initializeVariables === "function") {
          currentDrawer._initialized = false;
          currentDrawer.initializeVariables();
        }
      }

      if (window.location.pathname !== targetPath) {
        window.history.replaceState({}, "", targetUrl);
      }
    } catch (err) {
      console.error("⚠️ [VTO] Error updating drawer-flow for variant:", err);
    }
  }

  function findVariantBySku(skuId) {
    if (!skuId) return null;
    const cleanTargetSku = String(skuId).trim().toLowerCase();
    const allVariants = [];
    const defaultUrl = initialProductPath || window.location.pathname;

    const customScript = document.getElementById("custom-product-variants-data");
    if (customScript) {
      try {
        const customVariants = JSON.parse(customScript.textContent);
        if (Array.isArray(customVariants)) {
          allVariants.push(...customVariants);
        }
      } catch (e) {}
    }

    const swatchEls = document.querySelectorAll("custom-product-as-variant[data-variant-sku]");
    swatchEls.forEach((el) => {
      const sku = el.getAttribute("data-variant-sku");
      const id = el.getAttribute("data-variant-id");
      const price = el.getAttribute("data-variant-price");
      const url = el.getAttribute("data-variant-url");
      if (sku && id) {
        allVariants.push({ sku, id: parseInt(id) || id, price: parseInt(price) || price, url: url || defaultUrl });
      }
    });

    const jsonScripts = document.querySelectorAll(
      'variant-radios script[type="application/json"], variant-selects script[type="application/json"], mm-variant-radios script[type="application/json"], mm-variant-selects script[type="application/json"], [data-product-variants-json]'
    );
    for (const script of jsonScripts) {
      try {
        const variants = JSON.parse(script.textContent);
        if (Array.isArray(variants)) {
          variants.forEach(v => {
            if (v && !v.url) {
              v.url = defaultUrl;
            }
          });
          allVariants.push(...variants);
        }
      } catch (e) {}
    }

    let match = allVariants.find(
      (v) => v && v.sku && String(v.sku).trim().toLowerCase() === cleanTargetSku
    );
    if (match) return match;

    match = allVariants.find((v) => {
      if (!v || !v.sku) return false;
      const vSku = String(v.sku).trim().toLowerCase();
      return vSku === cleanTargetSku + "-c04" || vSku.startsWith(cleanTargetSku + "-c") || vSku.startsWith(cleanTargetSku);
    });
    if (match) return match;

    return null;
  }

  function updateActiveProductVariant(skuId) {
    if (!skuId) return;
    const targetSku = String(skuId).trim();

    const targetVariant = findVariantBySku(targetSku);

    if (targetVariant) {
      const actualSku = targetVariant.sku || targetSku;
      if (window.VTO_STATE) {
        window.VTO_STATE.activeSku = actualSku;
        window.VTO_STATE.activeVariantId = targetVariant.id;
        window.VTO_STATE.activeVariantPrice = targetVariant.price;
      }

      // Remember the selected variant so the PDP UI can be repainted once, on
      // modal close, via the theme's own custom-product-as-variant mechanism.
      // We defer the repaint (rather than swapping #MainContent on every
      // frame-switch) to avoid repeated page fetches while the user spins
      // through colours inside the modal.
      vtoPendingVariantSku = actualSku;

      const idInputs = document.querySelectorAll(
        'input[name="id"], .product-variant-id, #vto-buy-slot input[name="id"]'
      );
      idInputs.forEach((input) => {
        input.value = targetVariant.id;
        if (targetVariant.available !== false) {
          input.removeAttribute("disabled");
        } else {
          input.setAttribute("disabled", "disabled");
        }
      });

      const mainProductIdInputs = document.querySelectorAll(
        '#main-product-id, input[name="properties[main-product-id]"], drawer-flow #main-product-id'
      );
      mainProductIdInputs.forEach((input) => {
        input.value = targetVariant.id;
      });

      const productForms = document.querySelectorAll("product-form");
      productForms.forEach((form) => {
        form.setAttribute("data-variant-id", targetVariant.id);
        if (targetVariant.sku) form.setAttribute("data-variant-sku", targetVariant.sku);
        if (targetVariant.price) form.setAttribute("data-variant-price", targetVariant.price);
      });

      const ctaButtons = document.querySelectorAll(
        ".custom-buy-button, .custom-atc-button, .vto-buy-frame, .vto-buy-lens, #vto-buy-slot .button"
      );
      ctaButtons.forEach((btn) => {
        btn.setAttribute("data-product-id", targetVariant.id);
        btn.setAttribute("data-variant-id", targetVariant.id);
        if (targetVariant.price) {
          btn.setAttribute("data-product-price", targetVariant.price);
        }
        if (targetVariant.sku) {
          btn.setAttribute("data-variant-sku", targetVariant.sku);
        }

        if (targetVariant.available !== false) {
          btn.removeAttribute("disabled");
          if (btn.classList.contains("custom-buy-button") || btn.classList.contains("vto-buy-frame")) {
            btn.textContent = "Buy Only Frame";
          } else if (btn.classList.contains("custom-atc-button") || btn.classList.contains("vto-buy-lens")) {
            btn.textContent = "Buy With Lens";
          }
        } else {
          btn.setAttribute("disabled", "disabled");
          btn.textContent = "Sold Out";
        }
      });

      const try3dButtons = document.querySelectorAll(".try3d-button");
      try3dButtons.forEach((btn) => btn.setAttribute("data-sku", actualSku));

      const variantPickers = document.querySelectorAll(
        "variant-radios, variant-selects, mm-variant-radios, mm-variant-selects"
      );

      variantPickers.forEach((picker) => {
        const radios = picker.querySelectorAll('input[type="radio"]');
        radios.forEach((radio) => {
          const val = radio.value;
          if (
            val === targetVariant.option1 ||
            val === targetVariant.option2 ||
            val === targetVariant.option3
          ) {
            if (!radio.checked) {
              radio.checked = true;
              radio.dispatchEvent(new Event("change", { bubbles: true }));
            }
          }
        });

        const selects = picker.querySelectorAll("select");
        selects.forEach((select) => {
          const options = Array.from(select.options);
          const matchingOpt = options.find(
            (opt) =>
              opt.value === targetVariant.option1 ||
              opt.value === targetVariant.option2 ||
              opt.value === targetVariant.option3
          );
          if (matchingOpt && select.value !== matchingOpt.value) {
            select.value = matchingOpt.value;
            select.dispatchEvent(new Event("change", { bubbles: true }));
          }
        });
      });

      updateDrawerFlowForVariant(targetVariant);

    } else {
      console.warn(`⚠️ [VTO] SKU ${targetSku} not found in any product variant list.`);
    }
  }

  window.updateActiveProductVariant = updateActiveProductVariant;

  if (typeof window.subscribe === "function" && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) {
    window.subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
      if (event && event.data && event.data.variant && event.data.variant.sku) {
        const newSku = event.data.variant.sku;
        console.log("📡 [VTO] PubSub variantChange detected:", newSku);
        if (window.VTO_STATE) {
          window.VTO_STATE.activeSku = newSku;
          window.VTO_STATE.activeVariantId = event.data.variant.id;
          window.VTO_STATE.activeVariantPrice = event.data.variant.price;
        }
        document.querySelectorAll(".try3d-button").forEach((btn) => btn.setAttribute("data-sku", newSku));
      }
    });
  }

  /* =========================
     👓 PDP SWATCH → VTO_STATE SYNC   (fix for stale modal SKU)
     ------------------------------------------------------------
     These color swatches are "product as variant" links, NOT native
     Shopify variants, so PUB_SUB_EVENTS.variantChange never fires for
     them and window.VTO_STATE.activeSku goes stale after a normal PDP
     swatch click. That made openVTO() re-open the LAST modal-selected
     frame instead of the one currently selected on the PDP.

     This capture-phase listener refreshes VTO_STATE the moment a swatch
     is clicked on the page, so the modal always opens on the current
     variant. It does NOT interfere with CustomProductAsVariant's own
     click handler (that still does the #MainContent repaint).
  ========================= */
  document.addEventListener("click", function (e) {
    const swatchEl = e.target.closest("custom-product-as-variant[data-variant-sku]");
    if (!swatchEl) return;

    const sku = swatchEl.getAttribute("data-variant-sku");
    const id = swatchEl.getAttribute("data-variant-id");
    const price = swatchEl.getAttribute("data-variant-price");
    if (!sku) return;

    window.VTO_STATE = window.VTO_STATE || {};
    window.VTO_STATE.activeSku = sku.trim();
    if (id) window.VTO_STATE.activeVariantId = parseInt(id) || id;
    if (price) window.VTO_STATE.activeVariantPrice = parseInt(price) || price;

    // Keep the 3D buttons in sync too, so a direct 3D-button click uses this SKU.
    document.querySelectorAll(".try3d-button").forEach((btn) => btn.setAttribute("data-sku", sku.trim()));

    console.log("👓 [VTO] PDP swatch clicked — VTO_STATE.activeSku updated to:", sku.trim());
  }, true); // capture phase — runs before/independent of the element's own handler

  /* =========================
     📨 POST MESSAGE LISTENER
     UNCHANGED — same frame-switch + IMAGE_CAPTURED_COMPLETE handling.
     (The new iframe still posts IMAGE_CAPTURED_COMPLETE per Part B Step 4.)
  ========================= */
  window.addEventListener("message", function (event) {
    const data = event.data;

    if (!data || typeof data === "string" && data.startsWith("{")) {
      try { /* skip JSON-like strings from other tools */ } catch(e) {}
    }

    if (data && typeof data === "object" && (data.type || data.message || data.pd || data.image_uploaded || data.sku_id || data.skuid || data.sku)) {
      console.log("📩 [VTO] postMessage received:", JSON.stringify(data, null, 2));
    }

    // Frame-switch / SKU change from the 3D iframe → sync variant state.
    let selectedSkuFromIframe = null;
    if (typeof data === "object" && data !== null) {
      if (data.sku_id) {
        selectedSkuFromIframe = data.sku_id;
      } else if (data.skuid) {
        selectedSkuFromIframe = data.skuid;
      } else if (data.sku) {
        selectedSkuFromIframe = data.sku;
      } else if (typeof data.message === "string" && data.message.includes("frame selected:")) {
        selectedSkuFromIframe = data.message.split("frame selected:")[1].trim();
      }
    } else if (typeof data === "string" && data.includes("frame selected:")) {
      selectedSkuFromIframe = data.split("frame selected:")[1].trim();
    }

    if (selectedSkuFromIframe) {
      console.log("👓 [VTO] Frame selected event received from iframe with SKU:", selectedSkuFromIframe);
      updateActiveProductVariant(selectedSkuFromIframe);
    }

    if (typeof data === "string") {
      const lowerData = data.toLowerCase();

      if (lowerData.includes("image uploaded") || lowerData.includes("imageuploaded")) {
        console.log("📸 Image uploaded event received (string)!");
        vtoState.imageUploaded = true;
        vtoState.toggleOn = true;
        const toggle = document.getElementById("vto-2d-toggle");
        if (toggle) toggle.checked = true;

        closeVTOModal();
        processAllVTOSkus(true);
        return;
      }
    }

    if (typeof data === "object" && data !== null) {

      // ✅ Main signal: image captured & uploaded (Part B Step 4).
      if (data.type === "IMAGE_CAPTURED_COMPLETE") {
        console.log("📸✅ IMAGE_CAPTURED_COMPLETE received!");
        console.log("📸 Full payload:", JSON.stringify(data.payload, null, 2));

        vtoState.imageUploaded = true;
        vtoState.toggleOn = true;
        const toggle = document.getElementById("vto-2d-toggle");
        if (toggle) toggle.checked = true;

        closeVTOModal();

        console.log("🚀 Processing VTO images after IMAGE_CAPTURED_COMPLETE...");
        processAllVTOSkus(true);
        return;
      }

      if (data.type === "image_uploaded" || data.message === "image uploaded" || data.event === "image_uploaded") {
        console.log("📸 Image uploaded event received (object)!");
        vtoState.imageUploaded = true;
        vtoState.toggleOn = true;
        const toggle = document.getElementById("vto-2d-toggle");
        if (toggle) toggle.checked = true;

        closeVTOModal();
        processAllVTOSkus(true);
        return;
      }

      if (data.type === "pd_result" || data.type === "PD_RESULT") {
        console.log("📊 PD Result received (fallback):", data);
        vtoState.pdResult = data.pd || data.PD || data.data || data.payload || data;
        savePDToSession(vtoState.pdResult);
        return;
      }

      if (data.image_uploaded || data.imageUploaded) {
        console.log("📸 Combined image uploaded + data received!");

        if (data.pd || data.PD || data.pd_result || data.pd_data) {
          vtoState.pdResult = data.pd_data || data.pd || data.PD || data.pd_result;
          savePDToSession(vtoState.pdResult);
        }

        closeVTOModal();
        processAllVTOSkus(true);
        return;
      }
    }
  });

  /* =========================
     🟢 GLOBAL CLICK HANDLER  (UNCHANGED)
  ========================= */
  document.addEventListener("click", function (e) {

    const retakeBtn = e.target.closest("#vto-retake-btn");
    if (retakeBtn) {
      e.preventDefault();
      e.stopPropagation();
      const allSkus = collectVTOSkus();
      const firstSku = (allSkus.length > 0) ? allSkus[0].skuid : null;
      if (firstSku) {
        openVTO(firstSku, "2D");
      } else {
        const anyCard = document.querySelector('marmeto-product-card[data-product-sku]');
        const fallbackSku = anyCard ? anyCard.getAttribute('data-product-sku') : null;
        if (fallbackSku) {
          openVTO(fallbackSku, "2D");
        }
      }
      return;
    }

    const btn2D = e.target.closest(".vto-button-container");
    if (btn2D) {
      console.log("🟢 2D CLICK");
      e.preventDefault();
      e.stopPropagation();
      openVTO(btn2D.getAttribute("data-sku"), "2D");
      return;
    }

    const btn3D = e.target.closest(".try3d-button");
    if (btn3D) {
      console.log("🔵 3D CLICK");
      openVTO(btn3D.getAttribute("data-sku"), "3D");
      return;
    }

    if (e.target.closest("#vto-close-btn") || e.target.closest(".vto-modal-overlay")) {
      console.log("❌ VTO CLOSE CLICK");
      closeVTOModal();
      return;
    }

    if (e.target.closest("#vto-pd-close-btn") || e.target.closest("#vto-pd-ok-btn")) {
      console.log("❌ PD POPUP CLOSE");
      closePDPopup();
      return;
    }

    if (e.target.closest(".vto-pd-popup__overlay")) {
      console.log("❌ PD POPUP OVERLAY CLOSE");
      closePDPopup();
      return;
    }
  });

  /* =========================
     🔁 RESTORE ON PAGE LOAD  (UNCHANGED)
  ========================= */
  console.log("🔁 VTO page load — image state cleared. PD in session:", !!vtoState.pdResult);

  /* =========================
     👁️ LOAD-PREVIOUS-PAGE OBSERVER
     UNCHANGED except re-apply now pulls from the rendered-image cache.
  ========================= */
  function initLoadPreviousObserver() {
    if (!vtoState.imageUploaded || !vtoState.userId) return;

    const loadPreviousEl = document.querySelector("load-previous-page");
    if (!loadPreviousEl) return;

    const container = loadPreviousEl.parentElement;
    if (!container) return;

    const lpObserver = new MutationObserver((mutations) => {
      let hasNewVTOCards = false;
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('marmeto-product-card[data-vto-2d="true"]')) {
            hasNewVTOCards = true;
          } else if (node.querySelector && node.querySelector('marmeto-product-card[data-vto-2d="true"]')) {
            hasNewVTOCards = true;
          }
        });
      });
      if (hasNewVTOCards) {
        console.log("🔁 Load-Previous-Page: new VTO cards detected — restoring images...");
        setTimeout(() => {
          reapplyRenderedImages(vtoState.processedSkus);
          applyToggleState();
        }, 300);
      }
    });

    lpObserver.observe(container, { childList: true, subtree: true });
    console.log("👁️ VTO load-previous-page observer active");
  }

  initLoadPreviousObserver();

  /* =========================
     👁️ INFINITE SCROLL OBSERVER
     UNCHANGED structure; new SKUs go through the encrypted submit path,
     already-rendered SKUs re-apply from the cache.
  ========================= */
  function debounce(fn, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function handleNewScrolledCards() {
    if (!vtoState.imageUploaded || !vtoState.userId) {
      console.log("⏭️ Scroll detected new VTO cards but no image uploaded yet — skipping API call.");
      return;
    }

    console.log("🔁 New VTO cards detected from infinite scroll — checking for unprocessed SKUs...");

    const allCurrentSkus = collectVTOSkus();
    const newSkus = allCurrentSkus.filter(
      s => !vtoState.processedSkus.includes(s.skuid)
    );

    if (newSkus.length > 0) {
      console.log(`📤 Submitting ${newSkus.length} new SKU(s):`, newSkus.map(s => s.skuid));
      const batches = batchSkus(newSkus);
      (async () => {
        for (const batch of batches) {
          const res = await submitTryOnBatch(batch, vtoState.userId);
          (res.submitted || []).forEach(id => {
            if (!vtoState.processedSkus.includes(id)) vtoState.processedSkus.push(id);
          });
        }
        applyToggleState();
      })();
    } else {
      console.log("♻️ All visible VTO SKUs already processed — re-applying existing images.");
      reapplyRenderedImages(vtoState.processedSkus);
      applyToggleState();
    }
  }

  const debouncedHandleNewScrolledCards = debounce(handleNewScrolledCards, 400);

  function initScrollObserver() {
    const productGrid = document.getElementById("product-grid");
    if (!productGrid) {
      console.warn("⚠️ #product-grid not found — infinite scroll observer not started.");
      return;
    }

    const observer = new MutationObserver((mutations) => {
      let hasNewVTOCards = false;

      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType !== 1) return;

          if (node.matches && node.matches('marmeto-product-card[data-vto-2d="true"]')) {
            hasNewVTOCards = true;
            return;
          }

          if (node.querySelector && node.querySelector('marmeto-product-card[data-vto-2d="true"]')) {
            hasNewVTOCards = true;
          }
        });
      });

      if (hasNewVTOCards) {
        debouncedHandleNewScrolledCards();
      }
    });

    observer.observe(productGrid, { childList: true, subtree: true });
    console.log("👁️ VTO infinite scroll observer active on #product-grid");
  }

  initScrollObserver();

});